import { initialData, demoUser } from '../data/seed';
import type {
  AllocationSchedule,
  AppData,
  Asset,
  AssetDocument,
  IssuePayload,
  LoginResponse,
  ReceiptPayload,
  ReturnPayload,
  StocktakePayload,
} from '../types';
import { monthIso, slug, uid } from '../utils/format';

const STORE_KEY = 'bis-asset-control-data-v1';
const TOKEN_KEY = 'bis-asset-control-token';

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const load = (): AppData => {
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) {
    const data = initialData();
    localStorage.setItem(STORE_KEY, JSON.stringify(data));
    return clone(data);
  }
  try {
    return JSON.parse(raw) as AppData;
  } catch {
    const data = initialData();
    localStorage.setItem(STORE_KEY, JSON.stringify(data));
    return clone(data);
  }
};

const save = (data: AppData) => localStorage.setItem(STORE_KEY, JSON.stringify(data));
const docNo = (prefix: string, documents: AssetDocument[]) => {
  const month = new Date().toISOString().slice(0, 7).replace('-', '');
  const count = documents.filter((d) => d.no.startsWith(`${prefix}-${month}`)).length + 1;
  return `${prefix}-${month}-${String(count).padStart(4, '0')}`;
};

const assetCode = (category: string, assets: Asset[]) => {
  const year = new Date().getFullYear();
  const categoryCode = slug(category).slice(0, 4) || 'CCDC';
  return `HO-${categoryCode}-${year}-${String(assets.length + 1).padStart(5, '0')}`;
};

const addActivity = (data: AppData, title: string, detail: string, type: AppData['activities'][number]['type']) => {
  data.activities.unshift({ id: uid('ACT'), title, detail, type, at: new Date().toISOString(), by: data.currentUser.name });
  data.activities = data.activities.slice(0, 60);
};

export const mockApi = {
  async login(employeeCode: string, password: string): Promise<LoginResponse> {
    await new Promise((resolve) => setTimeout(resolve, 350));
    const valid = (employeeCode.toUpperCase() === 'ADMIN01' && password === '123456') ||
      (employeeCode.toUpperCase() === 'NV001' && password === '123456');
    if (!valid) throw new Error('Mã nhân viên hoặc mật khẩu chưa đúng.');
    const token = uid('MOCK-TOKEN');
    localStorage.setItem(TOKEN_KEY, token);
    return { token, user: demoUser };
  },

  async logout() {
    localStorage.removeItem(TOKEN_KEY);
  },

  async bootstrap(): Promise<AppData> {
    await new Promise((resolve) => setTimeout(resolve, 120));
    return clone(load());
  },

  async resetDemo(): Promise<AppData> {
    const data = initialData();
    save(data);
    return clone(data);
  },

  async createReceipt(payload: ReceiptPayload): Promise<AssetDocument> {
    const data = load();
    const id = uid('DOC');
    const no = docNo('TN', data.documents);
    const status = payload.lines.some((line) => (line.actualQuantity ?? line.quantity) !== line.quantity) ? 'DISCREPANCY' : 'COMPLETED';
    const document: AssetDocument = {
      id,
      no,
      type: 'RECEIPT',
      status,
      date: payload.receivedDate,
      source: payload.source,
      destinationAreaId: payload.destinationAreaId,
      receiver: data.currentUser.name,
      createdBy: data.currentUser.name,
      lines: payload.lines.map((line) => ({ ...line, id: uid('LINE') })),
      checklist: payload.checklist,
      note: payload.note,
      createdAt: new Date().toISOString(),
    };

    payload.lines.forEach((line) => {
      const quantity = Math.max(1, Number(line.actualQuantity ?? line.quantity));
      const count = line.trackingMode === 'INDIVIDUAL' ? quantity : 1;
      for (let i = 0; i < count; i += 1) {
        const code = assetCode(line.category, data.assets);
        const asset: Asset = {
          id: uid('AST'),
          code,
          name: line.itemName,
          category: line.category,
          trackingMode: line.trackingMode,
          quantity: line.trackingMode === 'INDIVIDUAL' ? 1 : quantity,
          unit: line.unit,
          status: payload.quickReceive ? 'PENDING_CHECK' : 'IN_STOCK',
          condition: line.condition,
          currentAreaId: payload.quickReceive ? 'AREA-CHO' : payload.destinationAreaId,
          serial: line.trackingMode === 'INDIVIDUAL' && count === 1 ? line.serial : undefined,
          source: payload.source,
          receivedAt: payload.receivedDate,
          originalValue: line.trackingMode === 'INDIVIDUAL' ? line.originalValue / count : line.originalValue,
          allocationMonths: line.allocationMonths,
          allocatedAmount: 0,
          note: line.note,
          createdAt: new Date().toISOString(),
        };
        data.assets.push(asset);
        if (asset.originalValue > 0 && asset.allocationMonths > 0) {
          const schedule: AllocationSchedule = {
            id: uid('AL'),
            assetId: asset.id,
            assetCode: asset.code,
            assetName: asset.name,
            areaId: asset.currentAreaId,
            startMonth: monthIso(),
            months: asset.allocationMonths,
            totalValue: asset.originalValue,
            monthlyAmount: Math.round(asset.originalValue / asset.allocationMonths),
            allocatedPeriods: 0,
            allocatedAmount: 0,
            remainingAmount: asset.originalValue,
            status: 'ACTIVE',
          };
          data.allocations.push(schedule);
        }
      }
    });

    data.documents.unshift(document);
    addActivity(data, `Tiếp nhận ${no}`, `${payload.lines.length} dòng vật dụng từ ${payload.source}`, 'RECEIPT');
    save(data);
    return clone(document);
  },

  async createAsset(input: Omit<Asset, 'id' | 'code' | 'createdAt' | 'allocatedAmount'>): Promise<Asset> {
    const data = load();
    const asset: Asset = {
      ...input,
      id: uid('AST'),
      code: assetCode(input.category, data.assets),
      allocatedAmount: 0,
      createdAt: new Date().toISOString(),
    };
    data.assets.push(asset);
    addActivity(data, 'Tạo hồ sơ tài sản', `${asset.code} - ${asset.name}`, 'ASSET');
    save(data);
    return clone(asset);
  },

  async createIssue(payload: IssuePayload): Promise<AssetDocument> {
    const data = load();
    const selected = data.assets.filter((asset) => payload.assetIds.includes(asset.id));
    if (!selected.length) throw new Error('Chưa chọn tài sản để cấp phát.');
    selected.forEach((asset) => {
      asset.status = 'IN_USE';
      asset.currentAreaId = payload.destinationAreaId;
      asset.custodian = payload.receiver;
      asset.custodianCode = payload.receiverCode;
    });
    const no = docNo('CP', data.documents);
    const document: AssetDocument = {
      id: uid('DOC'), no, type: 'ISSUE', status: 'COMPLETED', date: payload.issueDate,
      destinationAreaId: payload.destinationAreaId, receiver: payload.receiver, receiverCode: payload.receiverCode,
      createdBy: data.currentUser.name, note: payload.note, checklist: payload.checklist, createdAt: new Date().toISOString(),
      lines: selected.map((asset) => ({
        id: uid('LINE'), assetId: asset.id, itemName: asset.name, category: asset.category, trackingMode: asset.trackingMode,
        quantity: asset.quantity, unit: asset.unit, condition: asset.condition, serial: asset.serial,
        originalValue: asset.originalValue, allocationMonths: asset.allocationMonths,
      })),
    };
    data.documents.unshift(document);
    addActivity(data, `Cấp phát ${no}`, `${selected.length} tài sản cho ${payload.receiver}`, 'ISSUE');
    save(data);
    return clone(document);
  },

  async createReturn(payload: ReturnPayload): Promise<AssetDocument> {
    const data = load();
    const selected = data.assets.filter((asset) => payload.assetIds.includes(asset.id));
    if (!selected.length) throw new Error('Chưa chọn tài sản để thu hồi.');
    selected.forEach((asset) => {
      asset.status = payload.condition === 'DAMAGED' ? 'REPAIR' : 'IN_STOCK';
      asset.condition = payload.condition;
      asset.currentAreaId = payload.destinationAreaId;
      asset.custodian = undefined;
      asset.custodianCode = undefined;
    });
    const no = docNo('TH', data.documents);
    const document: AssetDocument = {
      id: uid('DOC'), no, type: 'RETURN', status: 'COMPLETED', date: payload.returnDate,
      destinationAreaId: payload.destinationAreaId, createdBy: data.currentUser.name, note: payload.note,
      checklist: payload.checklist, createdAt: new Date().toISOString(),
      lines: selected.map((asset) => ({
        id: uid('LINE'), assetId: asset.id, itemName: asset.name, category: asset.category, trackingMode: asset.trackingMode,
        quantity: asset.quantity, unit: asset.unit, condition: payload.condition, serial: asset.serial,
        originalValue: asset.originalValue, allocationMonths: asset.allocationMonths,
      })),
    };
    data.documents.unshift(document);
    addActivity(data, `Thu hồi ${no}`, `${selected.length} tài sản về kho`, 'RETURN');
    save(data);
    return clone(document);
  },

  async createStocktake(payload: StocktakePayload): Promise<AssetDocument> {
    const data = load();
    const area = data.areas.find((item) => item.id === payload.areaId);
    const lines = payload.results.map((result) => {
      const asset = data.assets.find((item) => item.id === result.assetId)!;
      return {
        id: uid('LINE'), assetId: asset.id, itemName: asset.name, category: asset.category, trackingMode: asset.trackingMode,
        quantity: asset.quantity, actualQuantity: result.found ? result.actualQuantity : 0, unit: asset.unit,
        condition: result.condition, serial: asset.serial, originalValue: asset.originalValue, allocationMonths: asset.allocationMonths,
        note: result.note,
      };
    });
    const hasDiff = lines.some((line) => line.quantity !== line.actualQuantity || line.condition !== data.assets.find((a) => a.id === line.assetId)?.condition);
    const no = docNo('KK', data.documents);
    const document: AssetDocument = {
      id: uid('DOC'), no, type: 'STOCKTAKE', status: hasDiff ? 'DISCREPANCY' : 'COMPLETED', date: payload.date,
      sourceAreaId: payload.areaId, createdBy: data.currentUser.name, note: payload.note, lines,
      checklist: [{ id: uid('CHECK'), label: 'Đã kiểm tra toàn bộ tài sản trong khu vực', checked: true, required: true }],
      createdAt: new Date().toISOString(),
    };
    data.documents.unshift(document);
    addActivity(data, `Kiểm kê ${no}`, `${area?.name ?? 'Khu vực'} - ${hasDiff ? 'có chênh lệch' : 'khớp dữ liệu'}`, 'STOCKTAKE');
    save(data);
    return clone(document);
  },

  async runAllocation(month: string): Promise<{ updated: number; amount: number }> {
    const data = load();
    let updated = 0;
    let amount = 0;
    data.allocations.forEach((schedule) => {
      if (schedule.status !== 'ACTIVE' || schedule.allocatedPeriods >= schedule.months) return;
      const periodAmount = Math.min(schedule.monthlyAmount, schedule.remainingAmount);
      schedule.allocatedPeriods += 1;
      schedule.allocatedAmount += periodAmount;
      schedule.remainingAmount = Math.max(0, schedule.totalValue - schedule.allocatedAmount);
      if (schedule.remainingAmount <= 0 || schedule.allocatedPeriods >= schedule.months) schedule.status = 'COMPLETED';
      const asset = data.assets.find((item) => item.id === schedule.assetId);
      if (asset) asset.allocatedAmount = schedule.allocatedAmount;
      updated += 1;
      amount += periodAmount;
    });
    addActivity(data, `Tính phân bổ ${month}`, `${updated} lịch, tổng ${amount.toLocaleString('vi-VN')} đồng`, 'ALLOCATION');
    save(data);
    return { updated, amount };
  },
};
